/**
 * The agreed words for direct-mail campaigns
 * (docs/features/direct-mail-campaigns/01_product_brief.md, journey-review
 * glossary), the trail and campaign-ticket formatters, call outcomes, and the
 * one date-ordered list of a campaign's sends, mailings and call rounds.
 * Every page uses these maps rather than writing its own label, so one
 * concept has one name everywhere. Backend vocabulary (CadenceChannel,
 * PlanStepState, TouchSkipReason, CallOutcome) is turned into words here, on
 * the client.
 */

import type {
  AddressSourceKind,
  CadenceChannel,
  CallOutcome,
  CampaignGoal,
  CampaignPerson,
  CampaignPlanStep,
  CampaignPlanTier,
  CampaignTrailEntry,
  DispatchMethod,
  MailingPostMethod,
  PlanStepState,
  TouchSkipReason,
} from "../data/api-types";
import {
  followUpLabel,
  sendRows,
  shortStepName,
  type SendLabelInput,
} from "./campaign-sends";
import { formatShortDateOrDash } from "./formatters";

export const CAMPAIGN_GOAL_LABELS: Record<CampaignGoal, string> = {
  restart_conversations: "Restart conversations",
  win_orders: "Win orders",
  win_new_product_type: "Win a new product type",
  book_meetings: "Book meetings",
};

export const CAMPAIGN_GOALS: CampaignGoal[] = [
  "restart_conversations",
  "win_orders",
  "win_new_product_type",
  "book_meetings",
];

/** The campaign's one-line summary: "Win orders · Holiday parks push", just
 *  the description on a campaign created before goals existed, and just the
 *  goal when there is no description. */
export function campaignGoalLine(campaign: {
  goal: CampaignGoal | null;
  description: string | null;
}): string {
  return [
    campaign.goal ? CAMPAIGN_GOAL_LABELS[campaign.goal] : null,
    campaign.description,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** What a step is called, by channel: a "Send" is an email. */
export const STEP_CHANNEL_LABELS: Record<CadenceChannel, string> = {
  email: "Send",
  direct_mail: "Mailing",
  call: "Call round",
  enclosure: "Enclosure",
};

export const STEP_STATE_LABELS: Record<PlanStepState, string> = {
  draft: "Draft",
  ready: "Ready",
  posted: "Posted",
  sent: "Sent",
  open: "Open",
};

export const ADDRESS_SOURCE_LABELS: Record<AddressSourceKind, string> = {
  person: "Own address",
  company: "Company address",
  none: "No address",
};

export const CONSENT_LABELS: Record<
  "allowed" | "suppressed" | "no_marketing",
  string
> = {
  allowed: "OK",
  suppressed: "Opted out",
  no_marketing: "No marketing (Tharstern)",
};

const OPTED_OUT_OF: Record<CadenceChannel, string> = {
  email: "opted out of email",
  direct_mail: "opted out of post",
  call: "opted out of phone",
  enclosure: "opted out of enclosures",
};

/** Why a step leaves someone out, in the words the pages use: "opted out of
 *  post", "no address", "left the company". */
export function notIncludedReasonLabel(
  channel: CadenceChannel,
  reason: TouchSkipReason,
): string {
  switch (reason) {
    case "consent_suppressed":
      return OPTED_OUT_OF[channel];
    case "no_address":
      return "no address";
    case "inactive":
      return "left the company";
    case "not_in_audience":
      return "no longer in the audience";
    case "not_exported":
      return "not in the downloaded mailing file";
    case "handled_by_landing_page_ticket":
      return "already has an open landing-page ticket";
    case "address_returned":
      return "returned post, address needs fixing";
    case "public_site_shopper":
      return "a public-site shopper, never contacted";
  }
}

/** "Not included – opted out of post". */
export function notIncludedLabel(
  channel: CadenceChannel,
  reason: TouchSkipReason,
): string {
  return `Not included – ${notIncludedReasonLabel(channel, reason)}`;
}

export const CALL_OUTCOME_LABELS: Record<CallOutcome, string> = {
  spoke_interested: "Spoke – interested",
  spoke_not_now: "Spoke – not now",
  not_interested: "Not interested",
  meeting_booked: "Meeting booked",
  quote_or_order_requested: "Quote or order requested",
  no_answer_left_message: "No answer / left message",
  other: "Other",
};

/** The longest note an "Other" outcome may carry (the backend's limit). */
export const OTHER_OUTCOME_NOTE_MAX_LENGTH = 200;

/** Display order for the outcome picker and outcome tallies. */
export const CALL_OUTCOMES: CallOutcome[] = [
  "spoke_interested",
  "spoke_not_now",
  "not_interested",
  "meeting_booked",
  "quote_or_order_requested",
  "no_answer_left_message",
  "other",
];

/** A tally of call outcomes (outcome -> count, as the backend sends it) in
 *  display order, leaving out the outcomes nobody has. */
function outcomeMixEntries(
  mix: Record<string, number> | null | undefined,
): { outcome: CallOutcome; count: number }[] {
  if (!mix) return [];
  return CALL_OUTCOMES.filter((o) => (mix[o] ?? 0) > 0).map((outcome) => ({
    outcome,
    count: mix[outcome],
  }));
}

/** How many outcomes a tally holds in all. */
export function outcomeMixTotal(
  mix: Record<string, number> | null | undefined,
): number {
  return outcomeMixEntries(mix).reduce((sum, e) => sum + e.count, 0);
}

/** "Spoke – interested 2 · Meeting booked 1"; empty when none recorded. */
export function outcomeMixLine(
  mix: Record<string, number> | null | undefined,
): string {
  return outcomeMixEntries(mix)
    .map((e) => `${CALL_OUTCOME_LABELS[e.outcome]} ${e.count}`)
    .join(" · ");
}

/** One entry of what a campaign has done with a person, as a short phrase:
 *  "Sent 19 Oct (opened)", "Called 28 Oct: Meeting booked", "Call due 28
 *  Oct", "Mailing 1: Not included – no address". */
export function formatTrailEntry(entry: CampaignTrailEntry): string {
  switch (entry.kind) {
    case "posted":
      return `Posted ${formatShortDateOrDash(entry.date)}`;
    case "delivered":
      return `Delivered ${formatShortDateOrDash(entry.date)}`;
    case "returned":
      return `Returned ${formatShortDateOrDash(entry.date)} – bad address`;
    case "sent":
      return entry.engagement
        ? `Sent ${formatShortDateOrDash(entry.date)} (${entry.engagement.kind})`
        : `Sent ${formatShortDateOrDash(entry.date)}`;
    case "called":
      return entry.outcome
        ? `Called ${formatShortDateOrDash(entry.date)}: ${CALL_OUTCOME_LABELS[entry.outcome]}`
        : `Call due ${formatShortDateOrDash(entry.date)}`;
    case "not_included":
      return `${shortStepName(entry.label)}: ${
        entry.channel && entry.skip_reason
          ? notIncludedLabel(entry.channel, entry.skip_reason)
          : "Not included"
      }`;
  }
}

/** The whole trail on one line, " · " separated. */
export function formatTrail(trail: CampaignTrailEntry[]): string {
  return trail.length === 0
    ? "Nothing yet"
    : trail.map(formatTrailEntry).join(" · ");
}

/** A step's label inside its own campaign: "Holiday Parks 2026 — Mailing
 *  1 – TIME pack" -> "Mailing 1 – TIME pack". A lone email send is labelled
 *  with just the campaign's name, so it reads "Send". */
export function stepNameInCampaign(
  label: string,
  campaignName: string,
): string {
  if (label === campaignName) return STEP_CHANNEL_LABELS.email;
  const prefix = `${campaignName} — `;
  return label.startsWith(prefix) ? label.slice(prefix.length) : label;
}

/** What happened to the contact on one send, for a ticket's list. */
function ticketPhrase(entry: CampaignTrailEntry): string | null {
  switch (entry.kind) {
    case "posted":
      return `Posted ${formatShortDateOrDash(entry.date)}`;
    case "delivered":
      return `Delivered ${formatShortDateOrDash(entry.date)}`;
    case "returned":
      return `Returned ${formatShortDateOrDash(entry.date)}`;
    case "sent":
      if (!entry.engagement) return `Sent ${formatShortDateOrDash(entry.date)}`;
      return `${entry.engagement.kind === "clicked" ? "Clicked" : "Opened"} ${formatShortDateOrDash(entry.engagement.date)}`;
    case "called":
      return entry.outcome
        ? `${CALL_OUTCOME_LABELS[entry.outcome]} ${formatShortDateOrDash(entry.date)}`
        : `due ${formatShortDateOrDash(entry.date)}`;
    case "not_included":
      return null;
  }
}

/** One line of a campaign ticket's "This campaign" list. ``isEmail`` marks a
 *  send that went out by email, so the ticket can offer its preview. */
export interface CampaignTicketSendLine {
  stepId: string;
  text: string;
  isEmail: boolean;
}

/** A campaign ticket's "This campaign" list: one line per send that reached
 *  the contact, oldest first ("Mailing 1 – TIME pack · Posted 19 Oct"). */
export function campaignTicketSendLines(
  trail: CampaignTrailEntry[],
  campaignName: string,
): CampaignTicketSendLine[] {
  const linesByStep = new Map<
    string,
    { name: string; phrases: string[]; isEmail: boolean }
  >();
  for (const entry of trail) {
    const phrase = ticketPhrase(entry);
    if (!phrase) continue;
    const line = linesByStep.get(entry.step_id) ?? {
      name: stepNameInCampaign(entry.label, campaignName),
      phrases: [],
      isEmail: false,
    };
    line.phrases.push(phrase);
    if (entry.kind === "sent") line.isEmail = true;
    linesByStep.set(entry.step_id, line);
  }
  return [...linesByStep.entries()].map(
    ([stepId, { name, phrases, isEmail }]) => ({
      stepId,
      text: `${name} · ${phrases.join(" · ")}`,
      isEmail,
    }),
  );
}

/** A person's consent exceptions on a campaign. Only the exceptions are
 *  listed; "OK" on every channel says nothing. Tharstern NoMarketing means
 *  they are not contacted at all, on any channel. */
export function consentNotes(consent: CampaignPerson["consent"]): string[] {
  if (Object.values(consent).includes("no_marketing")) {
    return ["No marketing (Tharstern) – not contacted at all"];
  }
  const notes: string[] = [];
  if (consent.direct_mail === "suppressed") notes.push("Opted out of post");
  if (consent.email === "suppressed") notes.push("Opted out of email");
  if (consent.phone === "suppressed") notes.push("Opted out of phone");
  return notes;
}

export const DISPATCH_METHOD_LABELS: Record<DispatchMethod, string> = {
  royal_mail: "Royal Mail",
  dpd: "DPD",
  dx: "DX",
};

/** The "Mark as posted" choices, in order. */
export const MAILING_POST_METHOD_LABELS: Record<MailingPostMethod, string> = {
  royal_mail: "Royal Mail",
  dpd: "DPD",
  dx: "DX",
  some_of_each: "Some of each",
};

export const MAILING_POST_METHODS: MailingPostMethod[] = [
  "royal_mail",
  "dpd",
  "dx",
  "some_of_each",
];

/** "Posted 19 Oct by Royal Mail" / "Posted 19 Oct, some by Royal Mail and
 *  some by courier". */
export function mailingPostedLine(
  postedOn: string,
  method: MailingPostMethod | null,
): string {
  const posted = `Posted ${formatShortDateOrDash(postedOn)}`;
  if (!method) return posted;
  return method === "some_of_each"
    ? `${posted}, some by Royal Mail and some by courier`
    : `${posted} by ${DISPATCH_METHOD_LABELS[method]}`;
}

/** Whether a posted mailing's packs take tracking numbers: it went by DPD,
 *  DX, or some of each (Royal Mail is untracked). */
export function mailingTakesTrackingNumbers(
  method: MailingPostMethod | null,
): boolean {
  return method === "dpd" || method === "dx" || method === "some_of_each";
}

/** The tracking number field's placeholder for how the mailing went. */
export function trackingNumberPlaceholder(
  method: MailingPostMethod | null,
): string {
  if (method === "dpd") return "Add DPD tracking number";
  if (method === "dx") return "Add DX tracking number";
  return "Add DPD or DX tracking number";
}

/** A courier's delivery status (delivery_tracking's TrackingStatus), in the
 *  pages' words. */
const DELIVERY_STATUS_LABELS: Record<string, string> = {
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  attempted_delivery: "Delivery attempted",
  exception: "Problem with delivery",
  unknown: "Status unknown",
};

/** A posted pack's status line: "Returned – bad address", "Delivered 20
 *  Oct", "Out for delivery", "Not checked yet" for a number the scheduled
 *  refresh hasn't read, or null for an untracked pack. */
export function postedPackStatus(recipient: {
  tracking_number?: string | null;
  delivery_status?: string | null;
  delivered_at?: string | null;
  returned_at?: string | null;
}): string | null {
  if (recipient.returned_at) return "Returned – bad address";
  if (!recipient.tracking_number) return null;
  if (recipient.delivery_status === "delivered")
    return recipient.delivered_at
      ? `Delivered ${formatShortDateOrDash(recipient.delivered_at)}`
      : "Delivered";
  if (!recipient.delivery_status) return "Not checked yet";
  return DELIVERY_STATUS_LABELS[recipient.delivery_status] ?? "Status unknown";
}

/** "Delivered 3 · Returned 1" for a posted mailing; null when neither has
 *  happened yet. */
export function mailingDeliveryLine(step: {
  delivered_count?: number | null;
  returned_count?: number | null;
}): string | null {
  const parts = [
    step.delivered_count ? `Delivered ${step.delivered_count}` : null,
    step.returned_count ? `Returned ${step.returned_count}` : null,
  ].filter((part): part is string => part !== null);
  return parts.length ? parts.join(" · ") : null;
}

/** The longest artwork code a mailing's tier can carry (a studio note:
 *  which design or file that tier's people receive). */
export const MAX_ARTWORK_CODE_LENGTH = 40;

/** "Artwork codes: VIP – TIME-VIP · Prospect – TIME-STD", in tier order;
 *  null when the step has none. */
export function artworkCodesLine(
  step: Pick<CampaignPlanStep, "target_tier_ids" | "artwork_codes">,
  tiers: Pick<CampaignPlanTier, "id" | "name">[],
): string | null {
  const parts = step.target_tier_ids
    .filter((id) => step.artwork_codes[id])
    .map((id) => {
      const name = tiers.find((t) => t.id === id)?.name ?? id;
      return `${name} – ${step.artwork_codes[id]}`;
    });
  return parts.length > 0 ? `Artwork codes: ${parts.join(" · ")}` : null;
}

/** "Planned" or "Posted": how the calendar names a mailing's state. */
export function calendarMailingState(state: PlanStepState): string {
  return state === "posted" ? "Posted" : "Planned";
}

/** "1 recipient" / "15 recipients". */
export function recipientCountLabel(count: number): string {
  return `${count} recipient${count === 1 ? "" : "s"}`;
}

/** What choosing no tiers means, by kind of step. */
export function tierChoiceHelp(channel: CadenceChannel): string {
  return channel === "email"
    ? "Leave every tier unticked to send to everyone in the audience, as usual."
    : "It only goes to the tiers you tick. With none ticked it has no recipients.";
}

/** Shown wherever tiers would be picked on a campaign that has none yet. */
export const NO_TIERS_YET_NOTE =
  "Set tiers after creating the campaign, or upload people with a tier column.";

/** Who a step goes to: "VIP + Prospect", "Follow-up to Mailing 1", or —
 *  for a send with no tiers chosen — "Everyone in the audience". A mailing
 *  or call round with no tiers chosen goes to nobody. */
export function stepAudienceLabel(
  step: Pick<
    CampaignPlanStep,
    "channel" | "target_tier_ids" | "follows_step_id"
  >,
  tiers: Pick<CampaignPlanTier, "id" | "name">[],
  steps: Pick<CampaignPlanStep, "id" | "name" | "label">[] = [],
): string {
  if (step.follows_step_id) {
    const source = steps.find((s) => s.id === step.follows_step_id);
    return followUpLabel(source?.name ?? source?.label);
  }
  const names = step.target_tier_ids.map(
    (id) => tiers.find((t) => t.id === id)?.name ?? id,
  );
  if (names.length > 0) return names.join(" + ");
  return step.channel === "email"
    ? "Everyone in the audience"
    : "No tiers chosen";
}

/** Whether the campaign page shows tiers, mailings and call rounds: a
 *  campaign created with a goal, or one that already has tiers or a
 *  mailing or call round. An email campaign from before goals existed looks
 *  exactly as it always has. */
export function showsCampaignPlan(
  campaign: { goal: CampaignGoal | null },
  plan: {
    tiers: unknown[];
    steps: Pick<CampaignPlanStep, "channel">[];
  } | null,
): boolean {
  if (!plan) return false;
  return (
    campaign.goal !== null ||
    plan.tiers.length > 0 ||
    plan.steps.some((s) => s.channel === "direct_mail" || s.channel === "call")
  );
}

/** One row of a campaign's list of sends: an email send (a tranche or its
 *  reminder) or a mailing / call round from the plan. */
export type CampaignSendListItem<E> =
  { kind: "email"; step: E } | { kind: "plan"; step: CampaignPlanStep };

/** The email-send fields the merged list orders by. */
export interface EmailSendForList extends SendLabelInput {
  sent_at: string | null;
  scheduled_send_date: string | null;
}

/**
 * A campaign with a plan lists everything in one date-ordered list: each
 * email tranche (with its reminder straight after it, as on every campaign)
 * at its date, and each mailing and call round at its own date. Ties keep
 * the plan's own order.
 */
export function campaignSendListItems<E extends EmailSendForList>(
  emailSends: E[],
  planSteps: CampaignPlanStep[],
): CampaignSendListItem<E>[] {
  type Group = { date: string; rank: number; items: CampaignSendListItem<E>[] };
  const groups: Group[] = [];
  const unplannedRank = planSteps.length;
  for (const send of sendRows(emailSends)) {
    if (send.follows_step_id) {
      const tranche = groups.find((g) =>
        g.items.some(
          (i) => i.kind === "email" && i.step.id === send.follows_step_id,
        ),
      );
      if (tranche) {
        tranche.items.push({ kind: "email", step: send });
        continue;
      }
    }
    const planIndex = planSteps.findIndex((p) => p.id === send.id);
    groups.push({
      date:
        (planIndex >= 0 ? planSteps[planIndex].date : null) ??
        send.sent_at?.slice(0, 10) ??
        send.scheduled_send_date ??
        "9999",
      rank: planIndex >= 0 ? planIndex : unplannedRank,
      items: [{ kind: "email", step: send }],
    });
  }
  planSteps.forEach((step, index) => {
    if (step.channel !== "direct_mail" && step.channel !== "call") return;
    groups.push({
      date: step.date ?? "9999",
      rank: index,
      items: [{ kind: "plan", step }],
    });
  });
  return groups
    .sort((a, b) => a.date.localeCompare(b.date) || a.rank - b.rank)
    .flatMap((g) => g.items);
}
